import { FaWhatsapp } from "react-icons/fa";
import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

const SPRINTCORE_COLORS = [
  "#D08E00",
  "#E5A900",
  "#B77900",
  "#F6C453",
  "#FFD879",
  "#FFFFFF",
];

const CANVAS_MARGIN = 46;

interface SpeedLine {
  x: number;
  y: number;
  baseY: number;
  length: number;
  speed: number;
  opacity: number;
  thickness: number;
  color: string;
  wobble: number;
  phase: number;
  age: number;
  jagOffsets: number[];
  reforgeEvery: number;
}

function SpeedLinesCanvas({ active }: { active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number | null>(null);
  const linesRef = useRef<SpeedLine[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;

    const SEGMENTS = 5;

    function generateJagOffsets(intensity: number): number[] {
      const offsets: number[] = [];
      for (let i = 0; i <= SEGMENTS; i++) {
        const edgeDamp = i === 0 || i === SEGMENTS ? 0.15 : 1;
        offsets.push((Math.random() - 0.5) * intensity * edgeDamp);
      }
      return offsets;
    }

    function createLine(): SpeedLine {
      const color =
        SPRINTCORE_COLORS[Math.floor(Math.random() * SPRINTCORE_COLORS.length)];

      const y = Math.random() * H;
      const length = 26 + Math.random() * 40;
      const speed = 3.2 + Math.random() * 4.2;
      const opacity = 0.35 + Math.random() * 0.5;
      const thickness = 0.9 + Math.random() * 1.5;
      const wobble = (Math.random() - 0.5) * 1.2;
      const intensity = 3.5 + Math.random() * 4.5;

      return {
        x: W + length,
        y,
        baseY: y,
        length,
        speed,
        opacity,
        thickness,
        color,
        wobble,
        phase: Math.random() * Math.PI * 2,
        age: 0,
        jagOffsets: generateJagOffsets(intensity),
        reforgeEvery: 5 + Math.floor(Math.random() * 6),
      };
    }

    function spawnBurst() {
      for (let i = 0; i < 7; i++) {
        const line = createLine();
        line.x = W + line.length + Math.random() * 30;
        linesRef.current.push(line);
      }
    }

    let frame = 0;

    function draw() {
      ctx.clearRect(0, 0, W, H);

      if (frame % 4 === 0) {
        linesRef.current.push(createLine());
      }

      linesRef.current = linesRef.current.filter(
        (line) => line.x > -line.length - 10,
      );

      for (const line of linesRef.current) {
        line.x -= line.speed;
        line.age += 1;

        line.y =
          line.baseY +
          Math.sin(line.phase + line.age * 0.07) * (4 + line.wobble * 3);

        // pequeño "glitch" eléctrico en los quiebres intermedios
        if (line.age % line.reforgeEvery === 0) {
          for (let i = 1; i < SEGMENTS; i++) {
            line.jagOffsets[i] =
              (Math.random() - 0.5) * (3.5 + Math.random() * 4.5);
          }
        }

        const progress = (W - line.x) / W;

        const fade =
          progress < 0.15
            ? progress / 0.15
            : progress > 0.8
              ? 1 - (progress - 0.8) / 0.2
              : 1;

        const alpha = Math.max(0, line.opacity * fade);
        if (alpha <= 0) continue;

        ctx.save();

        ctx.globalAlpha = alpha;
        ctx.strokeStyle = line.color;
        ctx.lineWidth = line.thickness;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        ctx.shadowColor = line.color;
        ctx.shadowBlur = 4 + line.thickness * 2;

        ctx.beginPath();

        const dirX = -(line.length / SEGMENTS);
        for (let i = 0; i <= SEGMENTS; i++) {
          const px = line.x + dirX * i;
          const py = line.y + line.jagOffsets[i];

          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }

        ctx.stroke();

        ctx.globalAlpha = alpha * 0.9;
        ctx.strokeStyle = "#FFFFFF";
        ctx.lineWidth = Math.max(0.6, line.thickness * 0.4);
        ctx.shadowBlur = 0;
        ctx.stroke();

        ctx.restore();
      }

      frame++;
      animRef.current = requestAnimationFrame(draw);
    }

    if (active) {
      spawnBurst();
      draw();
    } else {
      linesRef.current = [];
      ctx.clearRect(0, 0, W, H);
    }

    return () => {
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
      }
    };
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      width={280}
      height={60}
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{
        borderRadius: "9999px",
      }}
    />
  );
}

interface PerimeterPoint {
  x: number;
  y: number;
  nx: number;
  ny: number;
}

interface BranchSeg {
  dx: number;
  dy: number;
}

interface Branch {
  anchorRel: number;
  segs: BranchSeg[];
  color: string;
}

interface Bolt {
  startIdx: number;
  count: number;
  jitter: number[];
  tangent: number[];
  born: number;
  life: number;
  color: string;
  thickness: number;
  branches: Branch[];
  reforge: number;
}

interface Spark {
  x: number;
  y: number;
  nx: number;
  ny: number;
  born: number;
  life: number;
  radius: number;
  color: string;
  drift: number;
}

function buildPerimeter(w: number, h: number, steps = 96): PerimeterPoint[] {
  const r = Math.min(w, h) / 2;
  const straight = Math.max(0, w - 2 * r);
  const halfArc = Math.PI * r;
  const total = straight * 2 + halfArc * 2;
  if (total <= 0) return [];

  const pointAt = (dIn: number): PerimeterPoint => {
    let d = dIn;
    if (d < straight) {
      return { x: r + d, y: 0, nx: 0, ny: -1 };
    }
    d -= straight;
    if (d < halfArc) {
      const angle = -Math.PI / 2 + (d / halfArc) * Math.PI;
      return {
        x: w - r + Math.cos(angle) * r,
        y: r + Math.sin(angle) * r,
        nx: Math.cos(angle),
        ny: Math.sin(angle),
      };
    }
    d -= halfArc;
    if (d < straight) {
      return { x: w - r - d, y: h, nx: 0, ny: 1 };
    }
    d -= straight;
    const angle = Math.PI / 2 + (d / halfArc) * Math.PI;
    return {
      x: r + Math.cos(angle) * r,
      y: r + Math.sin(angle) * r,
      nx: Math.cos(angle),
      ny: Math.sin(angle),
    };
  };

  const pts: PerimeterPoint[] = [];
  for (let i = 0; i < steps; i++) {
    pts.push(pointAt((i / steps) * total));
  }
  return pts;
}

function randomColor() {
  return SPRINTCORE_COLORS[
    Math.floor(Math.random() * SPRINTCORE_COLORS.length)
  ];
}

function buildBranchSegs(): BranchSeg[] {
  const segCount = 3 + Math.floor(Math.random() * 3);
  const segs: BranchSeg[] = [];
  let along = 0;
  for (let i = 0; i < segCount; i++) {
    along += 3 + Math.random() * 4;
    const spread = (Math.random() - 0.5) * (5 - i * 0.8);
    segs.push({ dx: along, dy: spread });
  }
  return segs;
}

interface CometStreak {
  anchorX: number;
  anchorY: number;
  born: number;
  life: number;
  maxLength: number;
  color: string;
  thickness: number;
  segments: number;
  jitter: number[];
  reforge: number;
  driftSpeed: number;
}

function buildJitter(segments: number): number[] {
  return Array.from({ length: segments + 1 }, () => 0);
}

function ElectricCanvas({
  active,
  targetRef,
}: {
  active: boolean;
  targetRef: RefObject<HTMLElement | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number | null>(null);
  const streaksRef = useRef<CometStreak[]>([]);
  const perimeterRef = useRef<PerimeterPoint[]>([]);
  const sizeRef = useRef({ w: 58, h: 58 });

  useEffect(() => {
    const target = targetRef.current;
    const canvas = canvasRef.current;
    if (!target || !canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const applySize = (w: number, h: number) => {
      sizeRef.current = { w, h };
      canvas.style.left = `${-CANVAS_MARGIN}px`;
      canvas.style.top = `${-CANVAS_MARGIN}px`;
      canvas.style.width = `${w + CANVAS_MARGIN * 2}px`;
      canvas.style.height = `${h + CANVAS_MARGIN * 2}px`;
      canvas.width = Math.round((w + CANVAS_MARGIN * 2) * dpr);
      canvas.height = Math.round((h + CANVAS_MARGIN * 2) * dpr);
      perimeterRef.current = buildPerimeter(w, h);
    };

    const rect = target.getBoundingClientRect();
    applySize(rect.width || 58, rect.height || 58);

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        if (cr.width > 0 && cr.height > 0) {
          applySize(cr.width, cr.height);
        }
      }
    });
    ro.observe(target);

    return () => ro.disconnect();
  }, [targetRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let frame = 0;

    function spawnStreak() {
      const perimeter = perimeterRef.current;
      if (perimeter.length === 0) return;
      const p = perimeter[Math.floor(Math.random() * perimeter.length)];
      const segments = 7;

      streaksRef.current.push({
        anchorX: p.x,
        anchorY: p.y,
        born: frame,
        life: 34 + Math.floor(Math.random() * 22),
        maxLength: 26 + Math.random() * 34,
        color: randomColor(),
        thickness: 1 + Math.random() * 1.6,
        segments,
        jitter: buildJitter(segments),
        reforge: 3 + Math.floor(Math.random() * 4),
        driftSpeed: 0.35 + Math.random() * 0.5,
      });
    }

    function draw() {
      const { w, h } = sizeRef.current;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w + CANVAS_MARGIN * 2, h + CANVAS_MARGIN * 2);
      ctx.translate(CANVAS_MARGIN, CANVAS_MARGIN);

      const perimeter = perimeterRef.current;

      // contorno base pulsante (igual que antes)
      if (perimeter.length > 0) {
        const pulse =
          0.55 + Math.sin(frame * 0.12) * 0.15 + Math.random() * 0.06;

        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        ctx.beginPath();
        perimeter.forEach((p, i) => {
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.closePath();

        ctx.strokeStyle = "#E5A900";
        ctx.lineWidth = 3;
        ctx.shadowColor = "#D08E00";
        ctx.shadowBlur = 16;
        ctx.globalAlpha = pulse * 0.6;
        ctx.stroke();

        ctx.shadowBlur = 4;
        ctx.strokeStyle = "#FFF3D6";
        ctx.lineWidth = 1;
        ctx.globalAlpha = pulse * 0.55;
        ctx.stroke();
        ctx.restore();
      }

      if (frame % 4 === 0 && streaksRef.current.length < 16) spawnStreak();

      streaksRef.current = streaksRef.current.filter(
        (s) => frame - s.born < s.life,
      );

      for (const streak of streaksRef.current) {
        const age = frame - streak.born;

        // recalibra la turbulencia periódicamente (efecto llama/chispa)
        if (age % streak.reforge === 0) {
          for (let i = 1; i <= streak.segments; i++) {
            const chaos = (i / streak.segments) * 5.5;
            streak.jitter[i] = (Math.random() - 0.5) * chaos;
          }
        }

        const lifeRatio = age / streak.life;
        const growth = Math.min(1, age / (streak.life * 0.35));
        const currentLength = streak.maxLength * growth;

        const fadeIn = Math.min(1, age / 5);
        const fadeOut = lifeRatio > 0.6 ? 1 - (lifeRatio - 0.6) / 0.4 : 1;
        const baseAlpha = Math.max(0, fadeIn * fadeOut);
        if (baseAlpha <= 0) continue;

        // arrastre hacia la derecha: da la sensación de velocidad
        const driftX = -age * streak.driftSpeed;

        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        for (let i = 0; i < streak.segments; i++) {
          const t0 = i / streak.segments;
          const t1 = (i + 1) / streak.segments;

          // más turbulencia hacia el final de la cola (como llama soplada)
          const flutter0 = Math.sin(age * 0.25 + i) * (1 + t0 * 2);
          const flutter1 = Math.sin(age * 0.25 + i + 1) * (1 + t1 * 2);

          const x0 = streak.anchorX + driftX - t0 * currentLength;
          const y0 = streak.anchorY + (streak.jitter[i] ?? 0) + flutter0;
          const x1 = streak.anchorX + driftX - t1 * currentLength;
          const y1 = streak.anchorY + (streak.jitter[i + 1] ?? 0) + flutter1;

          const segAlpha = baseAlpha * (1 - t0 * 0.92);
          const segWidth = Math.max(0.3, streak.thickness * (1 - t0 * 0.85));

          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.lineTo(x1, y1);
          ctx.strokeStyle = streak.color;
          ctx.lineWidth = segWidth * 2.2;
          ctx.shadowColor = streak.color;
          ctx.shadowBlur = 9;
          ctx.globalAlpha = segAlpha * 0.55;
          ctx.stroke();

          // núcleo blanco caliente (lo que le da el look eléctrico)
          ctx.shadowBlur = 0;
          ctx.strokeStyle = "#FFFDF6";
          ctx.lineWidth = Math.max(0.3, segWidth * 0.5);
          ctx.globalAlpha = segAlpha * 0.85;
          ctx.stroke();
        }

        ctx.restore();
      }

      frame++;
      animRef.current = requestAnimationFrame(draw);
    }

    if (active) {
      streaksRef.current = [];
      for (let i = 0; i < 8; i++) spawnStreak();
      draw();
    } else {
      streaksRef.current = [];
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute"
      style={{ left: -CANVAS_MARGIN, top: -CANVAS_MARGIN }}
    />
  );
}

export default function WhatsAppFloatButton() {
  const phone = "593959218750";

  const message = encodeURIComponent(
    "Hola, quiero recibir más información sobre los servicios de SprintCore.",
  );

  const [hovered, setHovered] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    window.open(
      `https://wa.me/${phone}?text=${message}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <>
      <style>{`
        @keyframes sprint-wa-glow {
          0%, 100% {
            box-shadow:
              0 0 18px rgba(208, 142, 0, 0.35),
              0 5px 28px rgba(0, 0, 0, 0.32);
          }

          50% {
            box-shadow:
              0 0 36px rgba(229, 169, 0, 0.6),
              0 8px 38px rgba(0, 0, 0, 0.40);
          }
        }

        @keyframes sprint-wa-icon {
          0% {
            transform: scale(1) rotate(0deg);
          }

          30% {
            transform: scale(1.15) rotate(-10deg);
          }

          65% {
            transform: scale(1.20) rotate(8deg);
          }

          100% {
            transform: scale(1.12) rotate(0deg);
          }
        }

        .sprint-wa-button {
          width: 58px;

          transition:
            width 450ms cubic-bezier(0.22, 1, 0.36, 1),
            padding 450ms cubic-bezier(0.22, 1, 0.36, 1),
            gap 450ms cubic-bezier(0.22, 1, 0.36, 1),
            transform 350ms cubic-bezier(0.22, 1, 0.36, 1),
            border-color 350ms ease,
            background 400ms ease;
        }

        .sprint-wa-button:hover {
          width: 205px;
          transform: scale(1.04);
        }

        .sprint-wa-hovered {
          animation: sprint-wa-glow 1.6s ease-in-out infinite;
        }

        .sprint-wa-icon-hovered {
          animation: sprint-wa-icon 550ms ease forwards;
        }

        .sprint-wa-text {
          max-width: 0;
          opacity: 0;
          transform: translateX(-8px);
          overflow: hidden;

          transition:
            max-width 450ms cubic-bezier(0.22, 1, 0.36, 1),
            opacity 300ms ease,
            transform 450ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .sprint-wa-button:hover .sprint-wa-text {
          max-width: 150px;
          opacity: 1;
          transform: translateX(0);
        }

        .sprint-wa-arrow {
          display: inline-block;
          transition: transform 450ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .sprint-wa-button:hover .sprint-wa-arrow {
          transform: translateX(4px);
        }

        @media (max-width: 767px) {
          .sprint-wa-button:hover {
            width: 58px;
            transform: scale(1.05);
          }

          .sprint-wa-text {
            display: none;
          }
        }
      `}</style>

      <div className="fixed bottom-6 right-6 z-[100]">
        <ElectricCanvas active={hovered} targetRef={buttonRef} />

        <button
          ref={buttonRef}
          data-cursor="hide"
          type="button"
          onClick={handleClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className={`
            sprint-wa-button
            relative
            flex h-[58px]
            items-center justify-center
            overflow-hidden
            rounded-full
            border
            ${
              hovered
                ? "sprint-wa-hovered border-[#FFD879]/70"
                : "border-[#FFD487]"
            }
          `}
          style={{
            gap: hovered ? "10px" : "0px",

            paddingLeft: hovered ? "18px" : "0px",
            paddingRight: hovered ? "18px" : "0px",

            background: hovered
              ? "linear-gradient(135deg, #191103 0%, #D08E00 100%)"
              : "linear-gradient(135deg, #222222 0%, #000000 100%)",
            boxShadow: hovered
              ? undefined
              : `
                0 0 0 1px rgba(255,255,255,0.04),
                0 8px 30px rgba(0,0,0,0.35),
                0 0 20px rgba(208,142,0,0.15)
              `,
          }}
          aria-label="Contactar a SprintCore por WhatsApp"
        >
          <SpeedLinesCanvas active={hovered} />

          {/* brillo interno */}
          <div
            className="
              pointer-events-none
              absolute inset-[1px]
              rounded-full
              bg-gradient-to-b
              from-white/10
              via-transparent
              to-black/10
            "
          />

          <FaWhatsapp
            size={23}
            className={`
              relative z-10
              shrink-0
              text-white
              ${hovered ? "sprint-wa-icon-hovered" : ""}
            `}
          />

          <span
            className="
              sprint-wa-text
              relative z-10
              hidden
              whitespace-nowrap
              text-[13px]
              font-semibold
              tracking-wide
              text-white
              md:inline-flex
              md:items-center
              md:gap-1.5
            "
          >
            Contáctanos
          </span>
        </button>
      </div>
    </>
  );
}
